import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import SEOHead from '../../components/common/SEOHead';
import { usePageSeo } from '../../hooks/useSeoConfig';

function Usage() {
  const { language } = useLanguage();

  const steps = [
    {
      id: 1,
      titleFr: "Étape 1",
      titleEn: "Step 1",
      subtitleFr: "Inscrivez-vous",
      subtitleEn: "Sign Up",
      descriptionFr: "La démo a fini de vous convaincre ? Alors créez votre compte pour profiter gratuitement du PSTAR ou pour souscrire à un de nos nombreux niveaux. Formation complète ou entraînement aux examens : vous trouverez ce dont vous avez besoin !",
      descriptionEn: "Has the demo convinced you? Then create your account to enjoy PSTAR for free or subscribe to one of our many levels. Complete training or exam practice: find what you need!",
      imageFr: "/img/utilisation/new/Étape 1.png",
      imageEn: "/img/utilisation/new/Step 1.png",
      imagePosition: "right"
    },
    {
      id: 2,
      titleFr: "Étape 2",
      titleEn: "Step 2",
      subtitleFr: "Trouvez votre formule",
      subtitleEn: "Find Your Plan",
      descriptionFr: "Sélectionnez votre niveau, choisissez votre formule (cours, examens, ou les deux), puis déterminez la durée de votre abonnement (1, 3 ou 9 mois). Débutez ensuite votre formation depuis l'onglet « Mes niveaux ».",
      descriptionEn: "Select your level, choose your plan (courses, exams, or both), then determine your subscription duration (1, 3, or 9 months). Start your training from the \"My Levels\" tab.",
      imageFr: "/img/utilisation/new/Étape 2.png",
      imageEn: "/img/utilisation/new/Step 2.png",
      imagePosition: "left"
    },
    {
      id: 3,
      titleFr: "Étape 3",
      titleEn: "Step 3",
      subtitleFr: "Commencez votre formation",
      subtitleEn: "Start Your Training",
      descriptionFr: "Vous pouvez interrompre et reprendre votre formation à n'importe quel moment sans perdre vos données. Avant ou pendant l'examen, téléchargez l'annexe nécessaire pour répondre à certaines questions.",
      descriptionEn: "You can pause and resume your training at any time without losing your data. Before or during the exam, download the necessary appendix to answer certain questions.",
      imageFr: "/img/utilisation/new/Étape 3.png",
      imageEn: "/img/utilisation/new/Step 3.png",
      imagePosition: "right"
    },
    {
      id: 4,
      titleFr: "Étape 4",
      titleEn: "Step 4",
      subtitleFr: "Étudiez facilement",
      subtitleEn: "Study Easily",
      descriptionFr: "Vous trouverez sur nizus les mêmes outils que chez Transports Canada : une grille pour naviguer dans l'examen ou le cours, un bouton pour passer, en un clic, du français à l'anglais (et vice versa), une barre de progression dans le temps, et plus encore.",
      descriptionEn: "You will find on nizus the same tools as Transport Canada: a grid to navigate through the exam or course, a button to switch, with one click, from French to English (and vice versa), a progress bar over time, and more.",
      imageFr: "/img/utilisation/new/Étape 4.png",
      imageEn: "/img/utilisation/new/Step 4.png",
      imagePosition: "left"
    },
    {
      id: 5,
      titleFr: "Étape 5",
      titleEn: "Step 5",
      subtitleFr: "Suivez votre progression",
      subtitleEn: "Track Your Progress",
      descriptionFr: "L'onglet « Mes statistiques » vous offre une multitude d'informations précieuses : la correction de chaque examen effectué, le temps consacré, votre note (par matière et globale) comparée à la note de passage de Transports Canada, une explication à chaque question, des références utiles…",
      descriptionEn: "The \"My Statistics\" tab provides you with valuable information: corrections for each exam taken, time spent, your score (by subject and overall) compared to Transport Canada's passing grade, explanations for each question, useful references...",
      imageFr: "/img/utilisation/new/Étape 5.png",
      imageEn: "/img/utilisation/new/Step 5.png",
      imagePosition: "right"
    },
    {
      id: 6,
      titleFr: "Étape 6",
      titleEn: "Step 6",
      subtitleFr: "Obtenez votre lettre de recommandation",
      subtitleEn: "Get Your Recommendation Letter",
      descriptionFr: "Une fois votre cours et vos examens validés, la lettre de recommandation requise pour passer l'examen de Transports Canada vous est immédiatement délivrée. Tous les détails concernant la délivrance de cette lettre sont dans notre FAQ.",
      descriptionEn: "Once your course and exams are validated, the recommendation letter required to take the Transport Canada exam is issued to you immediately. All details regarding the delivery of this letter are in our FAQ.",
      imageFr: "/img/utilisation/new/Étape 6.png",
      imageEn: "/img/utilisation/new/Step 6.png",
      imagePosition: "left"
    }
  ];

  const renderStep = (step) => {
    const title = language === 'fr' ? step.titleFr : step.titleEn;
    const subtitle = language === 'fr' ? step.subtitleFr : step.subtitleEn;
    const description = language === 'fr' ? step.descriptionFr : step.descriptionEn;
    const image = language === 'fr' ? step.imageFr : step.imageEn;

    const content = (
      <div className="w-full lg:w-1/2 flex flex-col justify-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
        <h4 className="text-xl font-semibold text-blue-700 mb-4">{subtitle}</h4>
        <p className="text-gray-600 leading-relaxed">{description}</p>
      </div>
    );

    const imageElement = (
      <div className="w-full lg:w-1/2 flex justify-center items-center">
        <img
          src={image}
          alt={subtitle}
          title={subtitle}
          className="max-w-full h-auto rounded-lg shadow-md"
        />
      </div>
    );

    return (
      <div key={step.id} className="flex flex-col lg:flex-row gap-8 items-center py-12 border-b border-gray-200 last:border-b-0">
        {step.imagePosition === 'left' ? (
          <>
            {imageElement}
            {content}
          </>
        ) : (
          <>
            {content}
            {imageElement}
          </>
        )}
      </div>
    );
  };

  const seo = usePageSeo('page_usage');

  return (
    <>
      <SEOHead
        title={language === 'fr' ? (seo.title_fr || 'Mode d\'emploi') : (seo.title_en || 'How to Use')}
        description={language === 'fr' ? seo.desc_fr : seo.desc_en}
        keywords={language === 'fr' ? seo.keywords_fr : seo.keywords_en}
        canonical="/usage"
        noindex
      />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="w-full bg-gradient-to-r from-blue-700 to-blue-600 py-12 flex flex-col items-center justify-center text-white">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              {language === 'fr' ? 'Utilisation et avantages' : 'Usage and Benefits'}
            </h1>
            <p className="text-xl text-blue-100">
              {language === 'fr' ? 'Une interface de travail simple et intuitive.' : 'A simple and intuitive work interface.'}
            </p>
          </div>
        </section>

        {/* Hero Image */}
        <section className="w-full bg-white py-8 flex justify-center">
          <div className="max-w-4xl px-6">
            <img
              src={language === 'fr' ? '/img/utilisation/Comment utiliser nizus.png' : '/img/utilisation/How to use nizus.png'}
              alt={language === 'fr' ? 'Comment utiliser nizus' : 'How to use nizus'}
              title={language === 'fr' ? 'Comment utiliser nizus' : 'How to use nizus'}
              className="w-full h-auto rounded-lg shadow-md"
            />
          </div>
        </section>

        {/* Steps Section */}
        <section className="w-full bg-white py-12 flex flex-col items-center justify-center">
          <div className="w-full max-w-5xl mx-auto px-6">
            {steps.map(step => renderStep(step))}
          </div>
        </section>


      </main>
    </>
  );
}

export default Usage;
